import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator
} from '@progress/kendo-data-query';
import dayjs from 'dayjs';
import {
  CpsDocumentMetadataValue,
  CpsField,
  CpsFieldType,
  CpsFilterRequest,
  CpsGlobalFilter,
  CpsSearchQueryRequest,
  CPS_FIELD_TYPE
} from '@cs-cms/types';
import {
  formatGroupedDateFilterValue,
  removeDateFiltersFromRequest,
  removeFieldFromCompositeFilter
} from '../globalFilters/groupedDateGlobalFilterUtils';
import { GlobalFilterState } from '../globalFilters/GlobalFilterState';

type CellFilterValue = Exclude<CpsDocumentMetadataValue, undefined>;

export interface CellValueFilterUpdate {
  queryRequest?: CpsSearchQueryRequest;
  compositeFilterDescriptor?: CompositeFilterDescriptor;
  requestedGlobalDateFieldName?: string;
}

export interface BuildCellValueFilterUpdateParams {
  fieldName: string;
  value: CellFilterValue;
  globalFilter: CpsGlobalFilter | undefined;
  hasFacets: boolean;
  groupedDateFieldNames: string[];
  queryRequest: CpsSearchQueryRequest | undefined;
  compositeFilterDescriptor: CompositeFilterDescriptor | undefined;
}

function isCompositeFilter(
  descriptor: FilterDescriptor | CompositeFilterDescriptor
): descriptor is CompositeFilterDescriptor {
  return 'filters' in descriptor;
}

function isFilterDescriptor(
  descriptor: FilterDescriptor | CompositeFilterDescriptor
): descriptor is FilterDescriptor {
  return 'field' in descriptor;
}

function toEqualityDescriptors(fieldName: string, value: CellFilterValue): FilterDescriptor[] {
  if (Array.isArray(value)) {
    return value.map(item => ({
      field: fieldName,
      operator: FilterOperator.EqualTo,
      value: item
    }));
  }

  return [
    {
      field: fieldName,
      operator: FilterOperator.EqualTo,
      value
    }
  ];
}

export function applyCellValueFilter(
  current: CompositeFilterDescriptor | undefined,
  fieldName: string,
  value: CellFilterValue
): CompositeFilterDescriptor {
  const fieldFilter: CompositeFilterDescriptor = {
    logic: 'or',
    filters: toEqualityDescriptors(fieldName, value)
  };

  const remaining = (current?.filters ?? []).filter(descriptor => {
    if (!isCompositeFilter(descriptor)) {
      return true;
    }

    const firstFilter = descriptor.filters[0];
    if (!firstFilter || !isFilterDescriptor(firstFilter)) {
      return true;
    }

    return firstFilter.field !== fieldName;
  });

  return {
    logic: current?.logic ?? 'and',
    filters: [...remaining, fieldFilter]
  };
}

export function getVisibleGlobalFilters(
  globalFilters: CpsGlobalFilter[] | undefined,
  allFields: CpsField[] | undefined
): CpsGlobalFilter[] {
  if (!globalFilters || !allFields) {
    return [];
  }

  const visibleFieldNames = new Set(allFields.filter(field => !field.technical).map(field => field.name));
  return globalFilters.filter(filter => visibleFieldNames.has(filter.name));
}

function isDateField(fieldType: CpsFieldType): boolean {
  return fieldType === CPS_FIELD_TYPE.DATE || fieldType === CPS_FIELD_TYPE.DATETIME;
}

function toFilterDate(value: CellFilterValue): Date | undefined {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }
    return parsed;
  }

  return undefined;
}

function toGlobalFilterValues(value: CellFilterValue, hasFacets: boolean): string[] | undefined {
  if (Array.isArray(value)) {
    if (hasFacets) {
      return value;
    }
    if (value.length === 1) {
      return [value[0]];
    }
    return undefined;
  }

  return [String(value)];
}

function resolveGlobalFilterOperator(fieldType: CpsFieldType, hasFacets: boolean): FilterOperator | undefined {
  if (
    hasFacets ||
    fieldType === CPS_FIELD_TYPE.BOOLEAN ||
    fieldType === CPS_FIELD_TYPE.NUMERIC ||
    fieldType === CPS_FIELD_TYPE.INT32 ||
    fieldType === CPS_FIELD_TYPE.INT64
  ) {
    return FilterOperator.EqualTo;
  }

  if (fieldType === CPS_FIELD_TYPE.TEXT || fieldType === CPS_FIELD_TYPE.MULTICHOICE) {
    return FilterOperator.Contains;
  }

  return undefined;
}

function buildScalarGlobalFilter(
  field: CpsGlobalFilter,
  value: CellFilterValue,
  hasFacets: boolean
): CpsFilterRequest | undefined {
  const values = toGlobalFilterValues(value, hasFacets);
  const operator = resolveGlobalFilterOperator(field.type, hasFacets);
  if (!values || !operator) {
    return undefined;
  }

  return {
    fieldName: field.name,
    operator,
    values,
    fieldType: field.type,
    isGlobal: true
  };
}

function buildGlobalDateFilters(field: CpsGlobalFilter, value: Date): CpsFilterRequest[] {
  const localDate = dayjs(value);
  const filterState: GlobalFilterState = {
    ...field,
    facetValues: undefined
  };

  return [
    {
      fieldName: field.name,
      operator: FilterOperator.GreaterThanOrEqual,
      values: [formatGroupedDateFilterValue(filterState, localDate.startOf('day').toDate())],
      fieldType: CPS_FIELD_TYPE.DATE,
      isGlobal: true
    },
    {
      fieldName: field.name,
      operator: FilterOperator.LessThanOrEqual,
      values: [formatGroupedDateFilterValue(filterState, localDate.endOf('day').toDate())],
      fieldType: CPS_FIELD_TYPE.DATE,
      isGlobal: true
    }
  ];
}

function withoutCompositeField(
  compositeFilterDescriptor: CompositeFilterDescriptor | undefined,
  fieldName: string
): CompositeFilterDescriptor | undefined {
  if (!compositeFilterDescriptor) {
    return undefined;
  }

  return removeFieldFromCompositeFilter(compositeFilterDescriptor, fieldName);
}

export function buildCellValueFilterUpdate(params: BuildCellValueFilterUpdateParams): CellValueFilterUpdate {
  const { fieldName, value, globalFilter, compositeFilterDescriptor } = params;

  if (!globalFilter) {
    return {
      compositeFilterDescriptor: applyCellValueFilter(compositeFilterDescriptor, fieldName, value)
    };
  }

  if (!params.queryRequest) {
    return {};
  }

  const clearedComposite = withoutCompositeField(compositeFilterDescriptor, fieldName);

  if (isDateField(globalFilter.type)) {
    const filterDate = toFilterDate(value);
    if (!filterDate) {
      return {};
    }

    return {
      queryRequest: {
        ...params.queryRequest,
        filters: [
          ...removeDateFiltersFromRequest(params.queryRequest.filters, params.groupedDateFieldNames),
          ...buildGlobalDateFilters(globalFilter, filterDate)
        ],
        skip: 0
      },
      compositeFilterDescriptor: clearedComposite,
      requestedGlobalDateFieldName: globalFilter.name
    };
  }

  const scalarFilter = buildScalarGlobalFilter(globalFilter, value, params.hasFacets);
  if (!scalarFilter) {
    return {};
  }

  const remainingFilters = (params.queryRequest.filters ?? []).filter(filter => filter.fieldName !== fieldName);

  return {
    queryRequest: {
      ...params.queryRequest,
      filters: [...remainingFilters, scalarFilter],
      skip: 0
    },
    compositeFilterDescriptor: clearedComposite
  };
}
